import type { SVGProps } from "react";

/**
 * Icons used by the works page. The three stat icons are stroked with the
 * cyan → violet brand gradient from the design, so they reference gradients
 * declared once per page by <WorksGradientDefs />.
 */
type IconProps = SVGProps<SVGSVGElement>;

export const STAT_GRADIENT_ID = "works-stat-gradient";
export const STAT_GRADIENT_ALT_ID = "works-stat-gradient-alt";

/** Renders the shared gradient definitions once (zero-size, no layout cost). */
export function WorksGradientDefs() {
  return (
    <svg width="0" height="0" aria-hidden="true" focusable="false" className="absolute">
      <defs>
        <linearGradient id={STAT_GRADIENT_ID} x1="0" y1="0" x2="0.35" y2="1">
          <stop offset="0" stopColor="#04D1F1" />
          <stop offset="0.55" stopColor="#0CA5F0" />
          <stop offset="1" stopColor="#7C51BD" />
        </linearGradient>
        <linearGradient id={STAT_GRADIENT_ALT_ID} x1="1" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#7C51BD" />
          <stop offset="0.6" stopColor="#4A6BD8" />
          <stop offset="1" stopColor="#04D1F1" />
        </linearGradient>
      </defs>
    </svg>
  );
}

function Icon({ children, ...props }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...props}
    >
      {children}
    </svg>
  );
}

/* ── Stat card icons (gradient stroked) ─────────────────────────────────── */

export function FolderIcon(props: IconProps) {
  return (
    <Icon stroke={`url(#${STAT_GRADIENT_ID})`} strokeWidth={1.7} {...props}>
      <path d="M2.75 8.5V6.75A2.75 2.75 0 0 1 5.5 4h3.02c.72 0 1.4.33 1.84.9l1.02 1.3h6.12A2.75 2.75 0 0 1 20.25 8.95V9" />
      <path d="M2.75 11.25A2.25 2.25 0 0 1 5 9h14a2.25 2.25 0 0 1 2.23 2.55l-.78 5.75A2.75 2.75 0 0 1 17.73 19.7H6.27a2.75 2.75 0 0 1-2.72-2.4l-.8-6.05z" />
    </Icon>
  );
}

export function UsersIcon(props: IconProps) {
  return (
    <Icon stroke={`url(#${STAT_GRADIENT_ID})`} strokeWidth={1.7} {...props}>
      <circle cx="13" cy="8.75" r="3.9" />
      <path d="M6.5 20.25c.6-3.35 3.25-5.35 6.5-5.35s5.9 2 6.5 5.35" />
      <path d="M8.6 4.1a3.1 3.1 0 1 0 0 5.9" />
    </Icon>
  );
}

export function StarIcon(props: IconProps) {
  return (
    <Icon stroke={`url(#${STAT_GRADIENT_ALT_ID})`} strokeWidth={1.7} {...props}>
      <path d="M12 3.4c.36 0 .69.2.85.53l2.05 4.16 4.6.67c.36.05.66.3.77.65s.02.73-.24.99l-3.33 3.24.79 4.57a1 1 0 0 1-1.45 1.05L12 17.1l-4.04 2.15a1 1 0 0 1-1.45-1.05l.79-4.57-3.33-3.24a1 1 0 0 1 .53-1.64l4.6-.67 2.05-4.16c.16-.33.49-.53.85-.53Z" />
    </Icon>
  );
}

/* ── Filter pill icons ──────────────────────────────────────────────────── */

/** "All" — the 2×2 rounded-square cluster from the design. */
export function CategoriesIcon(props: IconProps) {
  return (
    <Icon strokeWidth={1.6} {...props}>
      <rect x="3.25" y="3.25" width="7.5" height="7.5" rx="3" />
      <rect x="13.25" y="3.25" width="7.5" height="7.5" rx="3" />
      <rect x="3.25" y="13.25" width="7.5" height="7.5" rx="3" />
      <rect x="13.25" y="13.25" width="7.5" height="7.5" rx="3" />
    </Icon>
  );
}

/** "Ui Ux" — the Figma glyph. */
export function FigmaIcon(props: IconProps) {
  return (
    <Icon strokeWidth={1.4} {...props}>
      <path d="M9 2.75h3v6.5H9a3.25 3.25 0 0 1 0-6.5Z" />
      <path d="M12 2.75h3a3.25 3.25 0 0 1 0 6.5h-3z" />
      <path d="M9 9.25h3v6.5H9a3.25 3.25 0 0 1 0-6.5Z" />
      <path d="M9 15.75h3V18a3.25 3.25 0 1 1-3-3.25Z" />
      <circle cx="15" cy="12.5" r="3.25" />
    </Icon>
  );
}

/** "Web apps" — a bordered table grid. */
export function TableIcon(props: IconProps) {
  return (
    <Icon strokeWidth={1.6} {...props}>
      <rect x="3.25" y="3.25" width="17.5" height="17.5" rx="3" />
      <path d="M3.25 9.1h17.5M3.25 14.9h17.5M9.1 3.25v17.5M14.9 3.25v17.5" />
    </Icon>
  );
}

/** "Dashboards" — a bare trend line inside a rounded square. */
export function DashboardIcon(props: IconProps) {
  return (
    <Icon strokeWidth={1.6} {...props}>
      <rect x="2.75" y="2.75" width="18.5" height="18.5" rx="6" />
      <path d="M6.9 15.1 10.2 11l2.2 2.1 4.7-5.2" />
    </Icon>
  );
}

/** "Branding" — the vector pen-tool glyph: kite body, centre anchor, angled nib. */
export function PenNibIcon(props: IconProps) {
  return (
    <Icon strokeWidth={1.6} {...props}>
      <path d="M3.5 3.5 13.9 6.9a2.6 2.6 0 0 1 1.7 1.7l.9 2.7-4.6 4.6-2.7-.9a2.6 2.6 0 0 1-1.7-1.7z" />
      <circle cx="10.6" cy="10.6" r="2" />
      <path d="M9.2 9.2 3.7 3.7" />
      <rect
        x="14.9"
        y="15.9"
        width="5.4"
        height="3.4"
        rx="1.7"
        transform="rotate(45 17.6 17.6)"
      />
    </Icon>
  );
}

/** Long "view details" arrow — points to the reading direction's end. */
export function ArrowIcon(props: IconProps) {
  return (
    <Icon strokeWidth={1.6} {...props}>
      <path d="M19.25 12H4.75M10.5 6.25 4.75 12l5.75 5.75" />
    </Icon>
  );
}

/* ── Floating hero glyphs around the character ──────────────────────────── */

export function ChatBubbleIcon(props: IconProps) {
  return (
    <Icon strokeWidth={1.4} {...props}>
      <path d="M4 7.25A3.25 3.25 0 0 1 7.25 4h9.5A3.25 3.25 0 0 1 20 7.25v5.5A3.25 3.25 0 0 1 16.75 16H10.5l-4 3.4V16h-.25A2.25 2.25 0 0 1 4 13.75z" />
      <path d="M8.25 10h.01M12 10h.01M15.75 10h.01" strokeWidth={2.4} />
    </Icon>
  );
}

export function MailIcon(props: IconProps) {
  return (
    <Icon strokeWidth={1.4} {...props}>
      <rect x="2.75" y="5.25" width="18.5" height="13.5" rx="2.5" />
      <path d="m3.5 6.5 7.1 6.05a2.15 2.15 0 0 0 2.8 0L20.5 6.5" />
    </Icon>
  );
}

export function SupportIcon(props: IconProps) {
  return (
    <Icon strokeWidth={1.4} {...props}>
      <path d="M5 13.5v-1.75a7 7 0 0 1 14 0v1.75" />
      <rect x="2.75" y="11.75" width="4.25" height="6.25" rx="2.1" />
      <rect x="17" y="11.75" width="4.25" height="6.25" rx="2.1" />
      <path d="M9.75 15h.01M12 15h.01M14.25 15h.01" strokeWidth={2.2} />
    </Icon>
  );
}
